import {
  CheckIcon,
  CircleIcon,
  EyeIcon,
  EyeOffIcon,
} from '@langchain/untitled-ui-icons';
import { Alert, LinearProgress } from '@mui/joy';

import {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  ReactNode,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { z } from 'zod';

import { useSegmentAnalyticsContext } from '@/SegmentAnalyticsProvider';
import DiscordIcon from '@/components/Auth/icons/DiscordIcon.svg?react';
import GithubIcon from '@/components/Auth/icons/GithubIcon.svg?react';
import GoogleIcon from '@/components/Auth/icons/GoogleIcon.svg?react';
import { IconButton, Text } from '@/design-system';
import { SupabaseAuthStateChangeContext } from '@/hooks/useAuth/SupabaseAuthStateChangeContext.tsx';
import { useLogout } from '@/hooks/useLogout.tsx';
import { useSendSSOEmailVerification } from '@/hooks/useSwr.tsx';
import { cn } from '@/utils/tailwind.tsx';

import {
  useAuth,
  useMonitorAuth,
  useMonitorOAuthSessioned,
  useUserSSOInfo,
} from '../hooks/useAuth/useAuth.tsx';
import {
  basicManager,
  oauthManager,
  oauthProviderSpecificManager,
  supabase,
} from '../utils/backend-auth.tsx';
import {
  LastAuthProvider,
  useLastActiveProvider,
  useLastActiveProviderUpdate,
} from './Auth.utils.tsx';
import { IncidentBanner } from './Banners/IncidentBanner.tsx';
import { Logo } from './Logo/Logo.tsx';
import { RegionPicker } from './RegionPicker.tsx';
import { VideoBackgroundPageWrapper } from './VideoBackgroundPageWrapper.tsx';

const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .refine(
    (val) => /[a-z]/.test(val),
    'Password must contain at least one lowercase letter'
  )
  .refine(
    (val) => /[A-Z]/.test(val),
    'Password must contain at least one uppercase letter'
  )
  .refine(
    (val) => /[0-9]/.test(val),
    'Password must contain at least one number'
  )
  .refine(
    (val) => /[!@#$%^&*()_+\-=[\]{};':"|<>?,./`~]/.test(val),
    'Password must contain at least one special character (!@#$%^&*()_+-=[]{};\':"|<>?,./`~)'
  );

function PasswordValidationFeedback({ password }: { password: string }) {
  const checks = [
    { test: /[a-z]/.test(password), label: 'One lowercase letter' },
    { test: /[A-Z]/.test(password), label: 'One uppercase letter' },
    { test: /[0-9]/.test(password), label: 'One number' },
    {
      test: /[!@#$%^&*()_+\-=[\]{};':"|<>?,./`~]/.test(password),
      label: 'One special character (!@#$%^&*()_+-=[]{};\':"|<>?,./`~)',
    },
    { test: password.length >= 8, label: 'At least 8 characters' },
  ];

  return (
    <div className="mt-2 space-y-1 duration-300 ease-in-out animate-in fade-in">
      <ul className="mt-2">
        {checks.map((check, index) => (
          <li key={index} className={`mt-1.5 flex items-center`}>
            <div className="flex items-center gap-2">
              {check.test ? (
                <CheckIcon className="size-4 rounded-full bg-[var(--green-600)] p-[1px] text-[var(--white)]" />
              ) : (
                <CircleIcon className="size-4 text-placeholder" />
              )}
              <Text variant="md" className="text-secondary">
                {check.label}
              </Text>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const AuthButton = forwardRef<
  HTMLButtonElement,
  {
    children?: ReactNode;
    variant: 'primary' | 'secondary' | 'ghost';
  } & DetailedHTMLProps<
    ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  >
>(({ children, ...props }, ref) => {
  return (
    <button
      type="button"
      ref={ref}
      {...props}
      className={cn(
        'flex h-16 items-center justify-center gap-2 rounded-full py-3 text-center font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50',
        props.variant === 'primary' &&
          'bg-[#1C3C3C] text-white hover:bg-[#1C3C3C]/90 active:bg-[#1C3C3C]/70',
        props.variant === 'secondary' &&
          'border border-secondary hover:bg-secondary-hover active:bg-secondary',
        props.variant === 'ghost' &&
          'hover:bg-secondary-hover active:bg-secondary',
        props.className
      )}
    >
      {children}
    </button>
  );
});

export function AuthLayout(props: {
  children?: ReactNode;
  header?: ReactNode;
}) {
  return (
    <VideoBackgroundPageWrapper className="overflow-auto bg-[#1C3C3C] pt-[15vh]">
      <div className="flex flex-grow flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-12">
          <Logo size="lg" className="h-10 w-auto flex-shrink-0 text-white" />
          <IncidentBanner />

          <div
            className={cn(
              'w-[628px] overflow-hidden rounded-[46px] bg-background'
            )}
          >
            {props.header && (
              <div className="flex items-center justify-center border-b border-secondary bg-secondary py-2 dark:border-b-[#26272B] dark:bg-[#26272B99]">
                {props.header}
              </div>
            )}
            <div className={cn('p-14', props.header && 'pb-12')}>
              {props.children}
            </div>
          </div>
        </div>
      </div>

      <div className="h-10 flex-shrink-0" />
    </VideoBackgroundPageWrapper>
  );
}

const AUTH_SUPABASE_SOCIAL_PROVIDERS = [
  { provider: 'google' as const, icon: <GoogleIcon className="h-8 w-8" /> },
  {
    provider: 'github' as const,
    // icon needs to be modified to be centered
    icon: <GithubIcon className="ml-[3px] h-8 w-8" />,
  },
  {
    provider: 'discord' as const,
    icon: <DiscordIcon className="h-8 w-8" />,
  },
];

function AuthSocial({
  redirectTo,
  lastActive,
}: {
  redirectTo?: string;
  lastActive?: LastAuthProvider;
}) {
  const updateLastActive = useLastActiveProviderUpdate();
  if (!supabase) return null;

  return (
    <div className="flex w-full items-stretch justify-center gap-2">
      {AUTH_SUPABASE_SOCIAL_PROVIDERS.map(({ provider, icon }) => {
        return (
          <button
            type="button"
            key={provider}
            onClick={() => {
              updateLastActive(provider);
              supabase?.auth.signInWithOAuth({
                provider,
                options: { redirectTo },
              });
            }}
            className={cn(
              'group',
              'relative flex h-16 w-[104px] grow items-center justify-center gap-2 rounded-2xl border px-4 py-2.5',
              lastActive === provider
                ? 'border-[var(--purple-700)] hover:bg-[var(--purple-25)]'
                : 'border-primary hover:bg-primary-hover active:bg-primary'
            )}
          >
            {icon}
            {lastActive === provider && (
              <Text
                variant="xs"
                weight="medium"
                className="pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-primary px-2 py-0.5   text-purple group-hover:bg-[var(--purple-100)]"
              >
                LAST USED
              </Text>
            )}
          </button>
        );
      })}
    </div>
  );
}

function AuthDisclaimer() {
  return (
    <Text
      variant="xs"
      className="text-center text-[#1C3C3C] dark:text-brand-primary"
    >
      By continuing, you agree to our{' '}
      <a
        href="https://www.langchain.com/terms-of-service"
        className="underline hover:text-brand-tertiary"
        target="_blank"
        rel="noreferrer"
      >
        Terms of Service
      </a>{' '}
      and{' '}
      <a
        href="https://www.langchain.com/privacy-policy"
        className="underline hover:text-brand-tertiary"
        target="_blank"
        rel="noreferrer"
      >
        Privacy Policy
      </a>
      .
    </Text>
  );
}

function AuthEmailForm(props: {
  email?: string;
  setEmail?: (email: string) => void;
  passwordAutoComplete: 'current-password' | 'new-password';
  onSubmit: (email: string, password: string) => Promise<void>;
  action?: ReactNode;
  validatePassword?: boolean;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState('');
  const validation = useMemo(
    () => passwordSchema.safeParse(password),
    [password]
  );
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  });
  const [showPassword, setShowPassword] = useState(false);
  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(e) => {
        e.stopPropagation();
        e.preventDefault();

        if (isLoading) return;
        const form = e.currentTarget;

        const data = new FormData(form);
        const email = data.get('email');
        const password = data.get('password');

        if (typeof email !== 'string' || typeof password !== 'string') {
          throw new Error('Expected email and password to be strings');
        }

        setIsLoading(true);
        props.onSubmit(email, password).finally(() => {
          if (!isMounted.current) return;
          setIsLoading(false);
        });
      }}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <label htmlFor="email">
            <Text variant="md" weight="medium">
              Email
            </Text>
          </label>
          <div className="flex h-[64px] items-center gap-2 rounded-full border border-primary px-4 py-2.5 transition-all focus-within:border-brand">
            <input
              className="grow border-none bg-transparent"
              type="email"
              name="email"
              placeholder="Your email address"
              id="email"
              value={props.email}
              onChange={(e) => props.setEmail?.(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="password">
            <Text variant="md" weight="medium">
              Password
            </Text>
          </label>
          <div className="flex h-[64px] items-center gap-2 rounded-full border border-primary px-4 py-2.5 transition-all focus-within:border-brand">
            <input
              className="grow border-none bg-transparent"
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="password"
              placeholder="Your password"
              autoComplete={props.passwordAutoComplete}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              aria-invalid={!validation.success}
            />
            <IconButton
              icon={showPassword ? EyeIcon : EyeOffIcon}
              label="Show password"
              tooltipProps={{
                side: 'bottom',
              }}
              onClick={() => setShowPassword(!showPassword)}
              variant="plain"
              color="secondary"
            />
          </div>
          {props.validatePassword && password.length > 0 && (
            <PasswordValidationFeedback password={password} />
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <AuthButton type="submit" variant="primary" disabled={isLoading}>
          Continue
        </AuthButton>

        {props.action}
      </div>
    </form>
  );
}

export function AuthEmailLogin(props: {
  onForgotPassword: () => void;
  email: string;
  setEmail: (email: string) => void;
}) {
  const isMounted = useRef(true);
  const updateLastActive = useLastActiveProviderUpdate();
  const [loginAttempt, setLoginAttempt] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  });

  return (
    <>
      {error && <Alert color="danger">{error}</Alert>}

      <AuthEmailForm
        email={props.email}
        setEmail={props.setEmail}
        passwordAutoComplete="current-password"
        onSubmit={async (email, password) => {
          if (!supabase) return;
          setIsLoading(true);
          setLoginAttempt((prev) => prev + 1);
          const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (error && isMounted.current) {
            setError(error.message);
          } else {
            updateLastActive('email');
          }
          setIsLoading(false);
        }}
        action={
          loginAttempt > 0 &&
          !isLoading && (
            <AuthButton
              type="button"
              variant="ghost"
              onClick={props.onForgotPassword}
              className="duration-300 ease-in-out animate-in fade-in"
            >
              Forgot password?
            </AuthButton>
          )
        }
      />
    </>
  );
}

export function AuthEmailSignup(props: {
  email: string;
  setEmail: (email: string) => void;
}) {
  const isMounted = useRef(true);

  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  });

  return (
    <>
      {error && <Alert color="danger">{error}</Alert>}

      {message && <Alert color="info">{message}</Alert>}

      <AuthEmailForm
        email={props.email}
        setEmail={props.setEmail}
        passwordAutoComplete="new-password"
        onSubmit={async (email, password) => {
          if (!supabase) return;
          setError(null);

          const {
            data: { user: signUpUser, session: signUpSession },
            error,
          } = await supabase.auth.signUp({
            email,
            password,
          });

          if (error) {
            setError(error.message);
          } else if (signUpUser && !signUpSession) {
            setMessage('Check your inbox for the confirmation email.');
          }
        }}
        validatePassword
      />
    </>
  );
}

export function AuthForgottenPassword(props: {
  redirectTo: string;
  onRevert: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [isLoading, setLoading] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  });

  return (
    <>
      {error && <Alert color="danger">{error}</Alert>}

      {message && <Alert color="info">{message}</Alert>}

      <form
        className="flex flex-col gap-6"
        onSubmit={async (e) => {
          e.stopPropagation();
          e.preventDefault();

          if (!supabase || isLoading) return;
          setLoading(true);
          try {
            const form = e.currentTarget;

            const data = new FormData(form);
            const email = data.get('email');

            if (typeof email !== 'string') {
              throw new Error('Expected email to be strings');
            }

            const { error } = await supabase.auth.resetPasswordForEmail(email, {
              redirectTo: props.redirectTo,
            });

            if (error) {
              setError(error.message);
            } else {
              setMessage('Check your email for the confirmation link');
            }
          } finally {
            if (isMounted.current) {
              setLoading(false);
            }
          }
        }}
      >
        <div className="flex flex-col gap-2">
          <label className="text-tertiary" htmlFor="email">
            Email
          </label>
          <input
            className="rounded-lg border-secondary bg-transparent outline-none transition-all focus-within:border-brand"
            type="email"
            name="email"
            id="email"
          />
        </div>

        <div className="flex flex-col gap-2">
          <AuthButton type="submit" variant="primary" disabled={isLoading}>
            Send reset instructions
          </AuthButton>

          <AuthButton type="button" variant="ghost" onClick={props.onRevert}>
            Return to Login
          </AuthButton>
        </div>
      </form>
    </>
  );
}

export function AuthUpdatePassword(props: { onSuccess?: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const validation = useMemo(
    () => passwordSchema.safeParse(password),
    [password]
  );

  return (
    <>
      {error && <Alert color="danger">{error}</Alert>}

      <form
        className="flex flex-col gap-6"
        onSubmit={async (e) => {
          e.stopPropagation();
          e.preventDefault();

          const form = e.currentTarget;

          const data = new FormData(form);
          const password = data.get('password');

          if (typeof password !== 'string') {
            throw new Error('Expected password to be strings');
          }

          if (!supabase) return;
          const { error } = await supabase.auth.updateUser({ password });

          if (error) {
            setError(error.message);
          } else {
            props.onSuccess?.();
          }
        }}
      >
        <div className="flex flex-col gap-2">
          <label className="text-tertiary" htmlFor="password">
            Password
          </label>
          <input
            className="rounded-lg border-secondary bg-transparent outline-none transition-all focus-within:border-brand"
            type="password"
            name="password"
            id="password"
            autoComplete="new-password"
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            aria-invalid={!validation.success}
          />
          <PasswordValidationFeedback password={password} />
        </div>

        <div className="flex flex-col gap-2">
          <AuthButton type="submit" variant="primary">
            Reset password
          </AuthButton>
        </div>
      </form>
    </>
  );
}

enum AuthState {
  SIGN_IN = 'sign_in',
  SIGN_UP = 'sign_up',
  FORGOTTEN_PASSWORD = 'forgotten_password',
  SIGN_IN_SSO = 'sign_in_sso',
}

function isAuthState(value: string | null): value is AuthState {
  return !!value && value in AuthState;
}

function AuthSupabaseGuest(props: {
  isAfterUpdatePassword?: boolean;
  onAfterUpdatePasswordReset?: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const redirectTo = window.location.href;
  const { analytics } = useSegmentAnalyticsContext();

  const searchParams = new URLSearchParams(location.search.slice(1));
  const errorCode = searchParams.get('error_code');

  let errorDescription: string | null = null;
  if (errorCode?.startsWith('4')) {
    errorDescription = 'Invalid login credentials';
  } else if (errorCode?.startsWith('5')) {
    errorDescription = 'Internal server error, please try again later';
  } else if (errorCode) {
    errorDescription = 'Unknown error, please try again';
  }

  const mode = searchParams.get('mode');

  const { lastActive, hasSignedIn } = useLastActiveProvider();

  let defaultMode: AuthState = AuthState.SIGN_UP;

  if (props.isAfterUpdatePassword || hasSignedIn) {
    defaultMode = AuthState.SIGN_IN;
  }
  if (isAuthState(mode)) defaultMode = mode;

  const [view, _setView] = useState<AuthState>(defaultMode);

  const setView = (newView: typeof view) => {
    searchParams.delete('mode');
    navigate({ search: searchParams.toString() });
    _setView(newView);
    if (props.isAfterUpdatePassword) {
      props.onAfterUpdatePasswordReset?.();
    }
  };

  useEffect(() => {
    if (supabase) {
      const { data: authListener } = supabase.auth.onAuthStateChange(
        (event) => {
          if (event === 'USER_UPDATED') setView(AuthState.SIGN_IN);
        }
      );

      return () => authListener.subscription.unsubscribe();
    }
  }, []);

  const region = useMemo(() => {
    if (window.location.host === 'eu.smith.langchain.com') {
      return 'eu' as const;
    } else {
      return 'us' as const;
    }
  }, []);

  const [email, setEmail] = useState('');

  return (
    <AuthLayout
      header={
        window.location.host.endsWith('smith.langchain.com') && (
          <RegionPicker region={region} />
        )
      }
    >
      <div className="flex flex-col gap-6">
        <h2 className="mb-3 text-center text-4xl tracking-[-0.06em]">
          {view === AuthState.SIGN_IN
            ? 'Log In'
            : view === AuthState.SIGN_UP
            ? 'Create an Account'
            : view === AuthState.FORGOTTEN_PASSWORD
            ? 'Reset password'
            : ''}
        </h2>
        {errorDescription && <Alert color="danger">{errorDescription}</Alert>}
        <AuthSocial lastActive={lastActive} redirectTo={redirectTo} />
        <div className="grid grid-cols-[1fr,auto,1fr] items-center gap-6">
          <span className="h-px bg-[#1C3C3C] dark:bg-[var(--brand-900)]" />
          <Text variant="sm" className="text-[#1C3C3C] dark:text-brand-primary">
            or
          </Text>
          <span className="h-px bg-[#1C3C3C] dark:bg-[var(--brand-900)]" />
        </div>
        <div className="flex flex-col gap-6">
          {view === 'sign_in' ? (
            <>
              {props.isAfterUpdatePassword && (
                <Alert color="info">
                  Your password has been updated. You can now log in.
                </Alert>
              )}

              {errorDescription && (
                <Alert color="danger">{errorDescription}</Alert>
              )}

              <AuthEmailLogin
                email={email}
                setEmail={setEmail}
                onForgotPassword={() => setView(AuthState.FORGOTTEN_PASSWORD)}
              />
            </>
          ) : view === 'sign_up' ? (
            <AuthEmailSignup email={email} setEmail={setEmail} />
          ) : view === AuthState.FORGOTTEN_PASSWORD ? (
            <AuthForgottenPassword
              redirectTo={redirectTo}
              onRevert={() => setView(AuthState.SIGN_IN)}
            />
          ) : null}
          {view !== AuthState.FORGOTTEN_PASSWORD && (
            <div className="flex items-start justify-center gap-2 text-[#1C3C3C] dark:text-brand-primary">
              <Text>
                {view === AuthState.SIGN_IN
                  ? "Don't have an account?"
                  : 'Already have an account?'}
              </Text>
              <button
                type="button"
                onClick={() => {
                  const viewToSwitchTo =
                    view === AuthState.SIGN_IN
                      ? AuthState.SIGN_UP
                      : AuthState.SIGN_IN;
                  if (viewToSwitchTo === AuthState.SIGN_UP) {
                    analytics?.track('Switch to Sign Up Panel Clicked');
                  }
                  setView(viewToSwitchTo);
                }}
                className={cn(
                  'hover:text-brand-tertiary',
                  'border-b border-b-[#1C3C3C] hover:border-b-brand-strong dark:border-b-[var(--black)]  dark:hover:border-b-[var(--brand-500)]'
                )}
              >
                <Text weight="semibold">
                  {view === AuthState.SIGN_IN ? 'Sign up' : 'Log in'}
                </Text>
              </button>
            </div>
          )}
        </div>

        <AuthDisclaimer />
      </div>
    </AuthLayout>
  );
}

function SSOSendEmailVerification() {
  const { session } = useAuth();

  const [hasSentEmail, setHasSentEmail] = useState(false);
  const { trigger } = useSendSSOEmailVerification();
  const { ssoProviderId, error } = useUserSSOInfo();
  const logout = useLogout();

  const debouncedSend = useDebouncedCallback(trigger, 1000);

  useEffect(() => {
    if (session && ssoProviderId && !hasSentEmail) {
      debouncedSend({
        json: {
          email: session?.user.email,
          saml_provider_id: ssoProviderId,
        },
      });
      setHasSentEmail(true);
    }
  }, [session, ssoProviderId, hasSentEmail, debouncedSend]);

  if (error) {
    return (
      <AuthLayout>
        <div className="flex flex-col gap-4">
          <div className="text-red-500">{error.message}</div>
          <AuthButton variant="primary" onClick={() => logout()}>
            Logout
          </AuthButton>
        </div>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout>
      <div className="flex flex-col gap-4">
        Email verification sent to {session?.user.email}. If you did not receive
        one, you can resend it.
        <AuthButton
          onClick={() => {
            if (session && ssoProviderId) {
              debouncedSend({
                json: {
                  email: session?.user.email,
                  saml_provider_id: ssoProviderId,
                },
              });
              setHasSentEmail(true);
            }
          }}
          variant="primary"
        >
          Resend verification email
        </AuthButton>
        <AuthButton variant="secondary" onClick={() => logout()}>
          Logout
        </AuthButton>
      </div>
    </AuthLayout>
  );
}

function AuthSupabase(props: { children?: ReactNode }) {
  const { authed } = useAuth();
  const [isAfterUpdatePassword, setAfterUpdatePassword] = useState(false);
  const [isUpdatePassword, setUpdatePassword] = useState(false);
  const [lastEvent, setLastEvent] = useContext(SupabaseAuthStateChangeContext);
  useMonitorAuth();

  const {
    isSSOEmailVerified,
    isSSOLogin,
    isLoading: SSOEmailVerifiedLoading,
  } = useUserSSOInfo();

  useEffect(() => {
    if (lastEvent === 'PASSWORD_RECOVERY') {
      setUpdatePassword(true);
      setAfterUpdatePassword(false);
      setLastEvent(undefined);
    }
  }, [lastEvent, setUpdatePassword, setAfterUpdatePassword, setLastEvent]);

  if (isUpdatePassword) {
    return (
      <AuthLayout>
        <div className="flex flex-col gap-4">
          <h2 className="mb-3 text-center text-4xl">Update password</h2>
          <AuthUpdatePassword
            onSuccess={() => {
              setUpdatePassword(false);
              setAfterUpdatePassword(true);
            }}
          />
          <AuthButton variant="ghost" onClick={() => setUpdatePassword(false)}>
            Return to Log In
          </AuthButton>
        </div>
      </AuthLayout>
    );
  }

  if (authed) {
    if (SSOEmailVerifiedLoading) {
      return (
        <div>
          <LinearProgress />
        </div>
      );
    }

    if (!isSSOEmailVerified && !SSOEmailVerifiedLoading && isSSOLogin) {
      return <SSOSendEmailVerification />;
    }
    return props.children;
  }

  return (
    <AuthSupabaseGuest
      isAfterUpdatePassword={isAfterUpdatePassword}
      onAfterUpdatePasswordReset={() => setAfterUpdatePassword(false)}
    />
  );
}

function AuthOAuth(props: { children?: ReactNode }) {
  const { authed } = useAuth();
  const location = useLocation();
  const redirectTo = location.pathname + location.search;
  useMonitorOAuthSessioned();

  const [isLoading, setLoading] = useState(false);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  });

  const handleLogin = async () => {
    if (!oauthManager && !oauthProviderSpecificManager) {
      throw new Error(
        `Expected an oauth manager to be defined for auth type oauth`
      );
    }

    setLoading(true);

    try {
      localStorage.setItem('returnUrl', redirectTo);
      if (oauthProviderSpecificManager) {
        await oauthProviderSpecificManager.signIn();
      } else if (oauthManager) {
        await oauthManager.signinRedirect();
      }
    } catch (e) {
      throw new Error(
        `Failed to start login redirect: ${
          e instanceof Error ? e.message : 'Oauth error'
        }`
      );
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  if (authed) {
    return props.children;
  }

  return (
    <AuthLayout>
      <div className="flex flex-col gap-4">
        <AuthButton
          type="button"
          variant="secondary"
          onClick={() => handleLogin()}
          disabled={isLoading}
        >
          Log in via SSO
        </AuthButton>

        <AuthDisclaimer />
      </div>
    </AuthLayout>
  );
}

function AuthMixedLogin() {
  const [error, setError] = useState<string | null>(null);
  return (
    <AuthLayout>
      <div className="flex flex-col gap-4">
        {error && <Alert color="danger">{error}</Alert>}

        <AuthEmailForm
          passwordAutoComplete="current-password"
          onSubmit={async (email, password) => {
            if (!basicManager) return;

            try {
              await basicManager.signIn(email, password);
            } catch (error) {
              setError(error instanceof Error ? error.message : `${error}`);
            }
          }}
        />

        <AuthDisclaimer />
      </div>
    </AuthLayout>
  );
}

function AuthMixed(props: { children?: ReactNode }) {
  const { authed } = useAuth();

  if (authed) {
    return props.children;
  }

  return <AuthMixedLogin />;
}

export function Auth(props: { children?: ReactNode }) {
  const { loading } = useAuth();

  if (loading) {
    return <LinearProgress />;
  }

  if (oauthManager || oauthProviderSpecificManager) {
    return <AuthOAuth>{props.children}</AuthOAuth>;
  }

  if (basicManager) {
    return <AuthMixed>{props.children}</AuthMixed>;
  }

  if (supabase) {
    return <AuthSupabase>{props.children}</AuthSupabase>;
  }

  return props.children;
}
